import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { Navigation } from '../components/Navigation';
import { GlobalSearch } from '../components/GlobalSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, {
        withCredentials: true,
      });
      setClients(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching clients:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="max-w-2xl mx-auto p-4">
          <div className="text-text-secondary">Loading...</div>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-1" data-testid="clients-title">
              Clients
            </h1>
            <p className="text-text-secondary">{clients.length} total</p>
          </div>
          <button
            onClick={() => navigate('/clients/new')}
            className="bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2 flex items-center gap-2 transition-colors"
            data-testid="add-client-button"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        <GlobalSearch />

        {clients.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center" data-testid="clients-empty">
            <p className="text-text-secondary mb-4">No clients yet</p>
            <button
              onClick={() => navigate('/clients/new')}
              className="bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2 transition-colors"
            >
              Add Your First Client
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((client) => (
              <button
                key={client.id}
                onClick={() => navigate(`/clients/${client.id}`)}
                className="w-full bg-surface border border-border hover:border-primary/50 rounded-xl p-4 text-left transition-all"
                data-testid={`client-card-${client.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-background font-medium flex-shrink-0">
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-foreground truncate">{client.name}</h3>
                      <StatusBadge status={client.status} />
                    </div>
                    <p className="text-sm text-text-secondary truncate">
                      {client.session_type || 'No session type'}
                    </p>
                    {client.total_spent > 0 && (
                      <p className="text-xs text-success mt-1">Lifetime: ${client.total_spent.toFixed(2)}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <Navigation />
    </div>
  );
}
